import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/billingReport";

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const toMoney = (v) => {
  const n = Number(String(v ?? 0).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const KV = ({ label, value }) => (
  <div className="flex leading-6">
    <div className="w-[140px] font-semibold">{label}</div>
    <div className="w-[16px] text-center">:</div>
    <div className="flex-1">{value ?? ""}</div>
  </div>
);

export default function BillingReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ billId from state OR query
  const billId = useMemo(() => {
    const fromState = location?.state?.billId;
    if (fromState) return Number(fromState) || 0;
    const p = new URLSearchParams(window.location.search);
    return Number(p.get("billId")) || 0;
  }, [location]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [row, setRow] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      if (!billId) throw new Error("BillId not found. Open print from list page.");

      const { data } = await axios.get(`${API_BASE}/print/${billId}`);
      if (!data?.success) throw new Error(data?.message || "Failed to load bill");

      setRow(data.row || null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load bill";
      setErrMsg(msg);
      setRow(null);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ guard
  const r = row || {};

  // ✅ EXACT FIELD NAMES (your JSON)
  const billNo = r.fld_billno || "-";
  const billDate = fmtDDMMYYYY(r.fld_Crdate);
  const branchName = r.fld_branchName || "-";

  const vehicleName = r.fld_vehicle_name || "-";
  const vehicleCode = r.fld_vehicle_code || "-";
  const vehicleRegNo = r.fld_vehicle_regno || "-";
  const storeName = r.fld_storename || "-";

  const equipmentType = "Own"; // your row doesn't contain H/O flag. If you add later, map it.
  const billStatus = r.fld_IsApproved ? "Approved" : "Not Approved";

  // totals
  const totalHr = toMoney(r.fld_totalhrs);
  const totalKm = toMoney(r.fld_total_km_hr);
  const breakingHrs = toMoney(r.fld_breaking_hrs);

  // Amount section values (exact fields)
  const betaAmt = Number(r.fld_bata || 0);
  const progressAmt = Number(r.fld_total_progress_amnt || 0);

  const sparesPct = Number(r.fld_spares_deduction_per || 0);
  const sparesAmt = Number(r.fld_deducted_spares_amnt || 0);

  const tdsPct = Number(r.fld_tds_per || 0);
  const tdsAmt = Number(r.fld_tds_amnt || 0);

  const maintenanceAmt = Number(r.fld_maintenance_amnt || 0);
  const othersAmt = Number(r.fld_other_deduction_amnt || 0);

  // your row has breaking amount
  const breakingChargesAmt = Number(r.fld_breaking_amnt || 0);

  // you did not send a material deduction amount field in JSON
  // keeping it as 0 to match template row
  const materialDedAmt = 0;

  const grandTotal = Number(r.fld_balance_payable || 0);

  const tableRows = [
    { label: "Beta", pct: "", amount: betaAmt },
    { label: "Total Progress Amount", pct: "", amount: progressAmt },
    { label: "Spares Deduction", pct: `${sparesPct.toFixed(2)} %`, amount: sparesAmt },
    { label: "TDS", pct: `${tdsPct.toFixed(2)} %`, amount: tdsAmt },
    { label: "Maintenance", pct: "", amount: maintenanceAmt },
    { label: "Others", pct: "", amount: othersAmt },
    { label: "Material Deductions", pct: "", amount: materialDedAmt },
    { label: "Breaking Charges", pct: "", amount: breakingChargesAmt },
  ];

  return (
    <VehicleLayoutWrapper>
      <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
        {/* HeaderBar only for screen */}
        <div className="print:hidden">
          <HeaderBar
            title={<span className="font-dmserif text-3xl">VEHICLE BILLING REPORT</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />
        </div>

        <div className="w-full bg-white">
          {/* ✅ PRINT CSS */}
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Toolbar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `BillId: ${billId}`}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>
              <button
                onClick={() => window.print()}
                disabled={loading || !row}
                className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          {/* ✅ Template */}
          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    {/* Logo box */}
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;
                        infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  Vehicle Billing Report
                </div>

                {/* KV Section */}
                <div className="border-b border-black">
                  <div className="grid grid-cols-2">
                    {/* Left */}
                    <div className="p-2 border-r border-black text-[12px]">
                      <KV label="Bill No" value={billNo} />
                      <KV label="Vehicle Name" value={vehicleName} />
                      <KV label="Hired From" value={"-"} />
                      <KV label="Type" value={vehicleName} />
                      <div className="flex">
                        <div className="w-[140px] font-semibold">Total Hr</div>
                        <div className="w-[16px] text-center">:</div>
                        <div className="w-[120px]">{totalHr}</div>

                        <div className="w-[140px] font-semibold text-right pr-2">Total KM</div>
                        <div className="w-[16px] text-center">:</div>
                        <div className="flex-1">{totalKm}</div>
                      </div>
                    </div>

                    {/* Right */}
                    <div className="p-2 text-[12px]">
                      <KV label="Date" value={billDate} />
                      <KV label="Vehicle Code" value={vehicleCode} />
                      <KV label="Branch" value={branchName} />
                      <KV label="Equipment Type" value={equipmentType} />
                      <KV label="Bill Status" value={billStatus} />
                      <div className="flex">
                        <div className="w-[140px] font-semibold">Breaking Hrs</div>
                        <div className="w-[16px] text-center">:</div>
                        <div className="flex-1">{breakingHrs}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amount Table */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black text-left px-2 py-1 w-[65%]"></th>
                        <th className="border border-black text-center px-2 py-1 w-[5%]">₹</th>
                        <th className="border border-black text-right px-2 py-1 w-[30%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((tr) => (
                        <tr key={tr.label}>
                          <td className="border border-black px-2 py-1">
                            <div className="flex items-center justify-between">
                              <span>{tr.label}</span>
                              {tr.pct ? <span className="mr-6">{tr.pct}</span> : <span />}
                            </div>
                          </td>
                          <td className="border border-black text-center px-2 py-1">₹</td>
                          <td className="border border-black text-right px-2 py-1">{toMoney(tr.amount)}</td>
                        </tr>
                      ))}

                      <tr>
                        <td className="border border-black px-2 py-1 font-bold">Total</td>
                        <td className="border border-black text-center px-2 py-1 font-bold">₹</td>
                        <td className="border border-black text-right px-2 py-1 font-bold">
                          {toMoney(grandTotal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* optional details below table like remarks */}
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                    <div className="border border-black p-2">
                      <div className="font-semibold">Maintenance Remarks</div>
                      <div>{r.fld_maintenance_remarks || "-"}</div>
                    </div>
                    <div className="border border-black p-2">
                      <div className="font-semibold">Other Deduction Remarks</div>
                      <div>{r.fld_other_deduction_remarks || "-"}</div>
                    </div>
                    <div className="border border-black p-2">
                      <div className="font-semibold">Store</div>
                      <div>{storeName}</div>
                    </div>
                    <div className="border border-black p-2">
                      <div className="font-semibold">Vehicle Reg No</div>
                      <div>{vehicleRegNo}</div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            {row && (
              <div className="no-print mt-4 text-xs text-gray-600">
                <details>
                  <summary className="cursor-pointer">Debug: View raw API row</summary>
                  <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                    {JSON.stringify(row, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
